import { Sort } from 'components/domain/stockPricing/StockDisplay/StockDisplayUtils'
import {
  AggregatedInventoryArticle,
  INVENTORY_ZOOM_LEVELS,
  PTInventoryArticle,
  PTInventoryProduct,
  TAXATION_SCHEME,
} from 'shared'
import { create } from 'zustand'

export interface InventoryStoreState {
  ptInventoryLoaded: boolean
  setPtInventoryLoaded: (newPtInventoryLoaded: boolean) => void
  currentPtInventory: PTInventoryProduct[]
  setCurrentPtInventory: (newCurrentPtInventory: PTInventoryProduct[]) => void
  ptArticles: PTInventoryArticle[]
  setPtArticles: (newPtArticles: PTInventoryArticle[]) => void
  step: number
  setStep: (newStep: number) => void
  numConflicts: number
  setNumConflicts: (newNumConflicts: number) => void
  numSolvedConflicts: number
  setNumSolvedConflicts: (newNumSolvedConflicts: number) => void
  diffCurrentInventory: AggregatedInventoryArticle[]
  setDiffCurrentInventory: (newDiffCurrentInventory: AggregatedInventoryArticle[]) => void
  diffIncomingInventory: AggregatedInventoryArticle[]
  setDiffIncomingInventory: (newDiffIncomingInventory: AggregatedInventoryArticle[]) => void
  paddedLeftData: (AggregatedInventoryArticle | undefined)[]
  setPaddedLeftData: (newPaddedLeftData: (AggregatedInventoryArticle | undefined)[]) => void
  paddedRightData: (AggregatedInventoryArticle | undefined)[]
  setPaddedRightData: (newPaddedRightData: (AggregatedInventoryArticle | undefined)[]) => void
  matchingData: AggregatedInventoryArticle[]
  setMatchingData: (newMatchingData: AggregatedInventoryArticle[]) => void
  diffSelectedArticles: AggregatedInventoryArticle[]
  setDiffSelectedArticles: (newDiffSelectedArticles: AggregatedInventoryArticle[]) => void
  selectedSource: string
  setSelectedSource: (newSelectedSource: string) => void
  zoomLevel: string
  setZoomLevel: (newZoomLevel: string) => void
  visibility: string
  setVisibility: (newVisibility: string) => void
  sort: Sort
  setSort: (newSort: Sort) => void
  defaultTaxationScheme: TAXATION_SCHEME | undefined
  setDefaultTaxationScheme: (newStep: TAXATION_SCHEME) => void
  displayTaxationIcon: string | undefined
  setDisplayTaxationIcon: (newDisplay: string) => void
}

export const useInventoryStore = create<InventoryStoreState>()((set) => ({
  ptInventoryLoaded: false,
  setPtInventoryLoaded: (newPtInventoryLoaded) =>
    set((s) =>
      s.ptInventoryLoaded === newPtInventoryLoaded ? s : { ptInventoryLoaded: newPtInventoryLoaded }
    ),

  currentPtInventory: [],
  setCurrentPtInventory: (newCurrentPtInventory: PTInventoryProduct[]) =>
    set({ currentPtInventory: newCurrentPtInventory }),

  ptArticles: [],
  setPtArticles: (newPtArticles: PTInventoryArticle[]) => set({ ptArticles: newPtArticles }),

  step: 0,
  setStep: (newStep) => set((s) => (s.step === newStep ? s : { step: newStep })),

  numConflicts: 0,
  setNumConflicts: (newNumConflicts) =>
    set((s) => (s.numConflicts === newNumConflicts ? s : { numConflicts: newNumConflicts })),

  numSolvedConflicts: 0,
  setNumSolvedConflicts: (newNumSolvedConflicts) =>
    set((s) =>
      s.numSolvedConflicts === newNumSolvedConflicts
        ? s
        : { numSolvedConflicts: newNumSolvedConflicts }
    ),

  diffCurrentInventory: [],
  setDiffCurrentInventory: (newDiffCurrentInventory: AggregatedInventoryArticle[]) =>
    set({ diffCurrentInventory: newDiffCurrentInventory }),

  diffIncomingInventory: [],
  setDiffIncomingInventory: (newDiffIncomingInventory: AggregatedInventoryArticle[]) =>
    set({ diffIncomingInventory: newDiffIncomingInventory }),

  paddedLeftData: [],
  setPaddedLeftData: (newPaddedLeftData: (AggregatedInventoryArticle | undefined)[]) =>
    set({ paddedLeftData: newPaddedLeftData }),

  paddedRightData: [],
  setPaddedRightData: (newPaddedRightData: (AggregatedInventoryArticle | undefined)[]) =>
    set({ paddedRightData: newPaddedRightData }),

  matchingData: [],
  setMatchingData: (newMatchingData: AggregatedInventoryArticle[]) =>
    set({ matchingData: newMatchingData }),

  diffSelectedArticles: [],
  setDiffSelectedArticles: (newDiffSelectedArticles: AggregatedInventoryArticle[]) =>
    set({ diffSelectedArticles: newDiffSelectedArticles }),

  selectedSource: '',
  setSelectedSource: (newSelectedSource) =>
    set((s) =>
      s.selectedSource === newSelectedSource ? s : { selectedSource: newSelectedSource }
    ),

  zoomLevel: INVENTORY_ZOOM_LEVELS.LOCATION,
  setZoomLevel: (newZoomLevel) =>
    set((s) => (s.zoomLevel === newZoomLevel ? s : { zoomLevel: newZoomLevel })),

  visibility: 'all',
  setVisibility: (newVisibility) =>
    set((s) => (s.visibility === newVisibility ? s : { visibility: newVisibility })),

  sort: { by: 'card.name', direction: 'asc', type: 'string' } as Sort,
  setSort: (newSort) =>
    set((s) => {
      const same =
        s.sort.by === newSort.by &&
        s.sort.direction === newSort.direction &&
        s.sort.type === newSort.type
      if (same) return s
      try {
        localStorage.setItem('sortArticles', JSON.stringify(newSort))
      } catch {}
      return { sort: newSort }
    }),

  defaultTaxationScheme: undefined,
  setDefaultTaxationScheme: (newDefaultTaxationScheme: TAXATION_SCHEME) =>
    set({ defaultTaxationScheme: newDefaultTaxationScheme }),
  displayTaxationIcon: undefined,
  setDisplayTaxationIcon: (newDisplay) =>
    set((s) => (s.displayTaxationIcon === newDisplay ? s : { displayTaxationIcon: newDisplay })),
}))
